// ./src/components/features/MarketingChannels/ChannelLinkQrDrawer/ChannelLinkQrDrawer.tsx
//
// Shows the QR code + copyable URL for ONE marketing channel's event-specific
// tracking link (…/e/<slug>?ch=<code>).
//
// ⓘ ROADMAP #138: the QR + link block is the shared `ShareQrLink` (the Share drawer and the wizard's finish screen show
// the same one) — this drawer only adds which channel it is.
//
// ⓘ ROADMAP #130: a drawer (house rule — a dialog is an AppDrawer, not a modal). The event's channels page opens it on
// a URL stage (`/events/:eventId/marketing-channels/:channelId/qr`); the wizard's Channels step on local state, as it
// opens inside the wizard's own step.

import React from "react";
import { Stack, Text, Code } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppDrawer } from "@/components/generic";
import { ShareQrLink } from "@/components/features/EventShare/ShareQrLink";
import { qrFileName } from "@/components/features/EventShare/eventShareRules";

export interface ChannelLinkQrDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** The full tracking URL (…/e/<slug>?ch=<code>). */
  url: string | null;
  /** Channel display name, shown as the modal subtitle. */
  channelName?: string | null;
  /** The ?ch= marker, shown as a chip. */
  code?: string | null;
}

export const ChannelLinkQrDrawer: React.FC<ChannelLinkQrDrawerProps> = ({
  opened,
  onClose,
  url,
  channelName,
  code,
}) => {
  const { t } = useTranslation(["marketingChannels", "common"]);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      mobileFitContent
      title={t("marketingChannels:qr.title", { defaultValue: "Channel QR code" })}
    >
      <Stack gap="md" align="center">
        <Stack gap={2} align="center">
          {channelName && (
            <Text size="md" fw={600}>
              {channelName}
            </Text>
          )}
          {code && <Code>{`?ch=${code}`}</Code>}
        </Stack>

        <ShareQrLink
          url={url}
          fileName={qrFileName("channel", code)}
          qrAlt={t("marketingChannels:qr.alt", { defaultValue: "Channel QR code" })}
        />
      </Stack>
    </AppDrawer>
  );
};
